import { useAuth } from '@/context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePageVisibility } from './usePageVisibility';

export const useSupplierAuth = () => {
  const { user, session, isLoading, userRole, roleLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(true);
  const { wasHiddenRef, resetWasHidden } = usePageVisibility();
  
  // Cache para evitar queries repetidas
  const approvalCacheRef = useRef<{ userId: string; isApproved: boolean } | null>(null);

  const isSupplier = userRole === 'supplier';
  const isRolePending = Boolean(user) && userRole === null;

  // Check if supplier is approved with cache
  useEffect(() => {
    const checkApprovalStatus = async () => {
      // Se página voltou do foco e já temos cache para este usuário, usar cache
      if (wasHiddenRef.current && approvalCacheRef.current && approvalCacheRef.current.userId === user?.id) {
        setIsApproved(approvalCacheRef.current.isApproved);
        setApprovalLoading(false);
        resetWasHidden();
        return;
      }

      // Se já temos cache para este usuário, usar cache
      if (approvalCacheRef.current && user && approvalCacheRef.current.userId === user.id) {
        setIsApproved(approvalCacheRef.current.isApproved);
        setApprovalLoading(false);
        return;
      }

      if (user && isSupplier) {
        try {
          const { isSupplierApproved } = await import('@/utils/supabase-query');
          const approved = await isSupplierApproved(user.id);
          setIsApproved(approved);
          approvalCacheRef.current = { userId: user.id, isApproved: approved };
        } catch (error) {
          // Falha transitória (rede/RLS) NÃO pode expulsar o fornecedor para a
          // home: mantemos o estado indeterminado (null) e não redirecionamos.
          console.error('Error checking approval status:', error);
          setIsApproved(null);
        }
      } else {
        setIsApproved(null);
        // Limpar cache se não é fornecedor
        if (approvalCacheRef.current?.userId !== user?.id) {
          approvalCacheRef.current = null;
        }
      }
      setApprovalLoading(false);
    };

    if (!isLoading && !roleLoading) {
      checkApprovalStatus();
    }
  }, [user?.id, isSupplier, isLoading, roleLoading]);

  const redirectToLogin = () => {
    if (location.pathname === '/login') {
      return;
    }
    const returnTo = `${location.pathname}${location.search}`;
    localStorage.setItem('postLoginRedirect', returnTo);
    navigate(`/login?redirect=${encodeURIComponent(returnTo)}`);
  };

  return {
    user,
    session,
    userRole,
    isSupplier,
    isApproved,
    isLoading: isLoading || roleLoading || approvalLoading || isRolePending,
    redirectToLogin,
  };
};